/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const VARNAME = `${feature.getSettingValue('VARNAME')}`;
    const dataTypeValue = feature.getSettingValue('DATATYPE');
    const FEATURE = feature.getSettingValue('FEATURE');
    const POLL_INTERVAL = feature.getSettingValue('POLL_INTERVAL');
    const linkedFeature = project.getFeature(FEATURE);
    let DATATYPE = dataTypeValue;
    switch (dataTypeValue) {
        case 'string':
            DATATYPE = 'string';
            break;
        case 'float':
            DATATYPE = 'float';
            break;
        case 'integer':
            DATATYPE = 'int';
            break;
        case 'boolean':
            DATATYPE = 'bool';
            break;
        default:
            DATATYPE = 'int';
            break;
    }


    let defaultValue = feature.getSettingValue('DEFAULT');
    if (DATATYPE === 'string') {
        defaultValue = `"${defaultValue}"`;
    } else if (defaultValue === '') {
        defaultValue = '0';
    }

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        id: `declare_var_${VARNAME}`,
        template: `${DATATYPE} ${VARNAME}=${defaultValue};`,
    });

    // optimization for declaring changed handler
    let hasChangedHandler = false;
    for (let i = 0; i < project.features.length; i++) {
        const tmpFeature = project.features[i];
        let linkedFeatureName = '';
        if (tmpFeature.id === 'node_variable_changed') {
            if (tmpFeature.getSettingValue('VARIABLE') === feature.getID()) {
                hasChangedHandler = true;
                break;
            }
        } else if (tmpFeature.id === 'node_timer_completed') {
            const timer = project.getFeature(tmpFeature.getSettingValue('TIMER'));
            if (timer !== undefined) {
                linkedFeatureName = timer.getSettingValue('TIMER_NAME');
            }
        }
        if (linkedFeatureName !== '') {
            if (linkedFeatureName === VARNAME) {
                hasChangedHandler = true;
                break;
            }
        }
        // if cgg or web dashboard, check if variable is used in any of the widgets
        if (['cgg', 'web_dashboard'].includes(tmpFeature.id)) {
            const widgets = tmpFeature.getSettingValue('WIDGETS');
            for (let j = 0; j < widgets.length; j++) {
                if (widgets[j].getSettingValue('VARIABLE') === feature.getID()) {
                    hasChangedHandler = true;
                    break;
                }
            }
        }
    }

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        id: `var_${VARNAME}_update_declare`,
        template: `void var_${VARNAME}_update();`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        id: `var_${VARNAME}_update`,
        blockType: BlockType.LOCATION,
        template: `
void var_${VARNAME}_update() {
    /****var_${VARNAME}_update****/
}`,
    });

    const MIN = feature.getSettingValue('MINIMUM');
    const MAX = feature.getSettingValue('MAXIMUM');
    let minCode = '';
    let maxCode = '';
    if (MIN !== '') {
        if (DATATYPE !== TibboDataType.STRING) {
            minCode = `
    if (value < ${MIN}) {
        value = ${MIN};
    }`;
        }
    }
    if (MAX !== '') {
        if (DATATYPE !== TibboDataType.STRING) {
            maxCode = `
    if (value > ${MAX}) {
        value = ${MAX};
    }`;
        }
    }

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        id: `var_${VARNAME}_update_completed`,
        blockType: BlockType.LOCATION,
        template: `
void var_${VARNAME}_update_completed(${DATATYPE} value) {
    ${DATATYPE} value_old = ${VARNAME};
    /****var_${VARNAME}_update_completed****/
    ${minCode}
    ${maxCode}
    ${VARNAME}=value;
    if (value_old != ${VARNAME}) {
        ${hasChangedHandler ? `var_${VARNAME}_changed(value_old);` : ''}
    }
    /****var_${VARNAME}_updated****/
}`,
    });

    let var_set_inner = '';
    if (linkedFeature && linkedFeature.id === 'stg_setting') {
        var_set_inner = `stg_set("${VARNAME}", ${project.convertDataType('value', DATATYPE, TibboDataType.STRING)});`;
    }

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        id: `var_${VARNAME}_set_declare`,
        template: `
void var_${VARNAME}_set(${DATATYPE} value);
void var_${VARNAME}_update_completed(${DATATYPE} value);`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        id: `var_${VARNAME}_set`,
        blockType: BlockType.LOCATION,
        template: `
void var_${VARNAME}_set(${DATATYPE} value) {
    ${minCode}
    ${maxCode}
    ${DATATYPE} value_old = ${VARNAME};
    ${VARNAME}=value;
    ${var_set_inner}
    
    /****var_${VARNAME}_set****/

    if (value_old != ${VARNAME}) {
        
        ${hasChangedHandler ? `var_${VARNAME}_changed(value_old);` : `
        /****var_${VARNAME}_changed****/`}
    }
}`,
    });

    if (hasChangedHandler) {
        blocks.push({
            location: 'FUNCTION_DECLARATIONS',
            id: `var_${VARNAME}_changed_declare`,
            template: `void var_${VARNAME}_changed(${DATATYPE} value_old);`,
        });
        blocks.push({
            location: 'FUNCTION_DEFINITIONS',
            id: `var_${VARNAME}_changed`,
            blockType: BlockType.LOCATION,
            template: `
void var_${VARNAME}_changed(${DATATYPE} value_old) {
    /****var_${VARNAME}_changed****/
}`,
        });
    }

    //
    // if ((linkedFeature && linkedFeature.id !== 'stg_setting') || !linkedFeature) {
    //     blocks.push({
    //         location: 'boot_complete',
    //         blockType: BlockType.CODE,
    //         template: `
    //     var_${VARNAME}_update_completed(${defaultValue})`,
    //     });
    // }

    if (POLL_INTERVAL === '') {
        blocks.push({
            location: 'var_poll_timer_elapsed',
            blockType: BlockType.CODE,
            template: `
    /****var_poll_timer_${VARNAME}_elapsed****/`,
        });
    } else {
        const INTERVAL = Number(POLL_INTERVAL) * 1000;
    
        blocks.push({
            location: 'FUNCTION_DEFINITIONS',
            blockType: BlockType.CODE,
            template: `
    void var_poll_timer_${VARNAME}() {
        /****var_poll_timer_${VARNAME}_elapsed****/
    }
    
    void timer_variable_handler_${VARNAME}(struct k_timer *timer_id) {
        var_poll_timer_${VARNAME}();   
    }
    `,
        });
    
        blocks.push({
            location: 'VARIABLE_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
    struct k_timer timer_var_${VARNAME};`,
        });
    
        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    k_timer_init(&timer_var_${VARNAME}, timer_variable_handler_${VARNAME}, NULL);
    k_timer_start(&timer_var_${VARNAME}, K_MSEC(${INTERVAL}), K_MSEC(${INTERVAL}));
    `,
        });
    }
    blocks.push({
        location: 'callback_luis_read',
        blockType: BlockType.CODE,
        template: `
if name="${VARNAME}" then
    value=${project.convertDataType(VARNAME, DATATYPE, TibboDataType.STRING)}
    callback_luis_read=true
end if`,
    });

    blocks.push({
        location: 'callback_luis_write',
        blockType: BlockType.CODE,
        template: `
if name="${VARNAME}" then
    ${VARNAME}=${project.convertDataType('value', TibboDataType.STRING, DATATYPE)}
    callback_luis_write=true
end if`,
    });

    return blocks;
};
